import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { Feature } from '@/types'

import { ArrowUpRight, GraduationCap, Presentation } from 'lucide-react'
import { buttonClass } from '@/lib/button'
import SectionHeading from '@/components/landing/section-heading'

import { signInSteps, studentPortalFeatures, trainerPortalFeatures } from '@/constants/content'

interface PortalCardProps {
    title: string
    description: string
    icon: LucideIcon
    features: Array<Feature>
}

const PortalCard: FC<PortalCardProps> = ({ title, description, icon: Icon, features }): ReactNode => {
    return (
        <article className='rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8'>
            <div className='flex items-center gap-4'>
                <span className='flex size-12 shrink-0 items-center justify-center rounded-xl bg-white/10 text-teal'>
                    <Icon className='size-6' aria-hidden='true' />
                </span>

                <div>
                    <h3 className='text-xl font-semibold text-white'>
                        {title}
                    </h3>

                    <p className='mt-0.5 text-sm text-white/60'>
                        {description}
                    </p>
                </div>
            </div>

            <ul className='mt-8 grid gap-6 sm:grid-cols-2'>
                {features.map((feature) => (
                    <li key={feature.title} className='flex gap-3'>
                        <feature.icon className='mt-0.5 size-5 shrink-0 text-teal' aria-hidden='true' />

                        <div>
                            <p className='text-sm font-semibold text-white'>
                                {feature.title}
                            </p>

                            <p className='mt-1 text-sm leading-relaxed text-white/60'>
                                {feature.text}
                            </p>
                        </div>
                    </li>
                ))}
            </ul>
        </article>
    )
}

const PortalSection: FC = (): ReactNode => {
    return (
        <section id='portal' aria-labelledby='portal-title' className='relative isolate scroll-mt-20 overflow-hidden bg-ink py-20 text-white sm:py-24 lg:py-28'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                <div className='hero-grid absolute inset-0 opacity-70' />
                <div className='absolute -right-40 -top-24 size-[32rem] rounded-full bg-primary/25 blur-[120px]' />
                <div className='absolute -bottom-40 -left-32 size-[28rem] rounded-full bg-teal/10 blur-[120px]' />
            </div>

            <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-8'>
                <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        id='portal-title'
                        tone='dark'
                        eyebrow="The Webify portal"
                        title="Your whole training, one sign-in away."
                        description="Students and trainers use the same portal, each with a view built for their role."
                    />

                    <a href='#sign-in' className={buttonClass('light', 'lg', 'group self-start lg:self-auto')}>
                        Go to sign-in
                        <ArrowUpRight className='size-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5' aria-hidden='true' />
                    </a>
                </div>

                <div className='mt-12 grid gap-5 lg:grid-cols-2'>
                    <PortalCard
                        title="For students"
                        description="Everything about your batch, from the first session to your certificate."
                        icon={GraduationCap}
                        features={studentPortalFeatures}
                    />

                    <PortalCard
                        title="For trainers"
                        description="Run your batches and keep every student on track."
                        icon={Presentation}
                        features={trainerPortalFeatures}
                    />
                </div>

                <div className='mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8'>
                    <h3 className='font-mono text-xs font-medium uppercase tracking-[0.2em] text-white/50'>
                        How to sign in
                    </h3>

                    <ol className='mt-5 grid gap-6 sm:grid-cols-3'>
                        {signInSteps.map((step, index) => (
                            <li key={step} className='flex gap-4'>
                                <span className='flex size-8 shrink-0 items-center justify-center rounded-full bg-teal font-mono text-sm font-semibold text-ink'>
                                    {index + 1}
                                </span>

                                <p className='pt-1 text-sm leading-relaxed text-white/75'>
                                    {step}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>
            </div>
        </section>
    )
}

export default PortalSection
